import { CategoryLinkList as CategoryLinkListUI } from '@portals/core/src/components/CategoryLinkList/CategoryLinkList';
import { FinalGridContainer } from '@portals/core/src/components/FinalGrid/FinalGrid';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import { trackCategoryLinkList } from '@portals/tracking/src/content/trackCategoryLinkList';
import { LinkTracking, TrackingContext } from '@portals/tracking/src/context/tracking';
import React, { useCallback } from 'react';

import { CategoryLinkList as CategoryLinkListModel } from '../../../models';
import { InternCategoryLink } from '../InternCategoryLink/InternCategoryLink';

export interface Props {
  node: CategoryLinkListModel;
}

export const CategoryLinkList: React.FC<Props> = ({ node }: Props) => {
  const handleTracking = useCallback(
    (payload: LinkTracking) => {
      trackCategoryLinkList({
        id: node.id,
        name: node.name,
        ...payload,
      });
    },
    [node.id, node.name]
  );
  const hasLeftColumn = node.left.length > 0 ? 1 : 0;
  const hasMiddleColumn = node.middle.length > 0 ? 1 : 0;
  const hasRightColumn = node.right.length > 0 ? 1 : 0;

  const hasTwoColumn = hasLeftColumn + hasMiddleColumn + hasRightColumn == 2;

  const columnWidthSM = hasLeftColumn && hasMiddleColumn && hasRightColumn ? 12 : hasTwoColumn ? 6 : 12;
  const columnWidth = hasLeftColumn && hasMiddleColumn && hasRightColumn ? 4 : hasTwoColumn ? 6 : 12;

  return (
    <TrackingContext.Provider value={{ trackLink: handleTracking }}>
      <FinalGridContainer>
        {hasLeftColumn ? (
          <CategoryLinkListUI
            sm={columnWidthSM}
            md={columnWidth}
            lg={columnWidth}
            data-selector={`${node.__contentType}.${node.id}.left`}
          >
            {node.leftHeadline ? (
              <Typography
                data-selector={`${node.leftHeadline.__component}.${node.leftHeadline.id}`}
                color="text.overline"
                component={node.leftHeadline.component}
                variant="h6"
                innerPadding="both"
                key={node.leftHeadline.id}
              >
                {node.leftHeadline.text}
              </Typography>
            ) : null}
            {node.left.map((element) => {
              return <InternCategoryLink key={element.id} node={element} />;
            })}
          </CategoryLinkListUI>
        ) : null}
        {hasMiddleColumn ? (
          <CategoryLinkListUI
            sm={columnWidthSM}
            md={columnWidth}
            lg={columnWidth}
            data-selector={`${node.__contentType}.${node.id}.middle`}
          >
            {node.middleHeadline ? (
              <Typography
                data-selector={`${node.middleHeadline.__component}.${node.middleHeadline.id}`}
                color="text.overline"
                component={node.middleHeadline.component}
                variant="h6"
                innerPadding="both"
              >
                {node.middleHeadline.text}
              </Typography>
            ) : null}
            {node.middle.map((element) => {
              return <InternCategoryLink key={element.id} node={element} />;
            })}
          </CategoryLinkListUI>
        ) : null}
        {hasRightColumn ? (
          <CategoryLinkListUI
            sm={columnWidthSM}
            md={columnWidth}
            lg={columnWidth}
            data-selector={`${node.__contentType}.${node.id}.right`}
          >
            {node.rightHeadline ? (
              <Typography
                data-selector={`${node.rightHeadline.__component}.${node.rightHeadline.id}`}
                color="text.overline"
                component={node.rightHeadline.component}
                variant="h6"
                innerPadding="both"
              >
                {node.rightHeadline.text}
              </Typography>
            ) : null}
            {node.right.map((element) => {
              return <InternCategoryLink key={element.id} node={element} />;
            })}
          </CategoryLinkListUI>
        ) : null}
      </FinalGridContainer>
    </TrackingContext.Provider>
  );
};
